import {
	Box,
	Button,
	keyframes,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import {
	type CurrencyEnum,
	PortfolioReportingOverviewTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyWidgetTooltip } from "web_myplann/Shared/Components/PropertyWidgetTooltip";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import type { DashboardType } from "./DashboardTitleCard";
import { EvaluationChip } from "./EvaluationChip";

const slideOutLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const slideInFromRight = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOutRight = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const slideInFromLeft = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

type AnimationDirection = "left" | "right" | null;

type DashboardAnimatedContentProps = {
	dashboardData: DashboardType;
	isAnimating: boolean;
	isPrevious: boolean;
	animationDirection: AnimationDirection;
	isDashboardDetail?: boolean;
	navigateToDashboardDetail?: () => void;
	onTooltipToggle?: (open: boolean) => void;
};

export const DashboardAnimatedContent = ({
	dashboardData,
	isAnimating,
	isPrevious,
	animationDirection,
	isDashboardDetail,
	navigateToDashboardDetail,
	onTooltipToggle,
}: DashboardAnimatedContentProps) => {
	const { t } = useResource();
	const { palette } = useTheme();
	const getAnimation = () => {
		if (!isAnimating) return "none";

		if (isPrevious) {
			return animationDirection === "right"
				? `${slideOutLeft} 0.3s ease-in-out`
				: `${slideOutRight} 0.3s ease-in-out`;
		}
		return animationDirection === "right"
			? `${slideInFromRight} 0.3s ease-in-out`
			: `${slideInFromLeft} 0.3s ease-in-out`;
	};

	return (
		<Box
			key={dashboardData.id}
			sx={{
				position: "absolute",
				width: "100%",
				top: 0,
				left: 0,
				bottom: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				animation: getAnimation(),
			}}
		>
			<Stack
				position="absolute"
				top={isDashboardDetail ? "60px" : "50px"}
				direction={"row"}
				alignItems={"center"}
			>
				{!isDashboardDetail && (
					<>
						<Typography
							fontSize={14}
							fontWeight={600}
							color={palette.darkmodeText.white}
							lineHeight={"20px"}
							zIndex={1}
						>
							{dashboardData.type}
						</Typography>
						<PropertyWidgetTooltip
							title={t(Resources.Dashboard.TitleCard.tooltip)}
							onToggle={onTooltipToggle}
							slotProps={{
								popper: {
									modifiers: [
										{ name: "offset", options: { offset: [-50, -20] } },
									],
								},
							}}
						/>
					</>
				)}
			</Stack>
			<Typography
				fontSize={32}
				fontWeight={700}
				color={palette.darkmodeText.white}
				position="absolute"
				top={isDashboardDetail ? "80px" : "70px"}
				zIndex={1}
			>
				{moneyFormatter(
					Math.round(dashboardData.value),
					t(
						Resources.PortfolioDetail.SharedLabels.Currency[
							dashboardData.currency as CurrencyEnum
						],
					),
					`0 ${t(
						Resources.PortfolioDetail.SharedLabels.Currency[
							dashboardData.currency as CurrencyEnum
						],
					)}`,
				)}
			</Typography>
			{dashboardData?.evaluation != null &&
				dashboardData.overviewType !==
					PortfolioReportingOverviewTypeEnum.Total && (
					<EvaluationChip
						evaluation={dashboardData.evaluation}
						isDashboardDetail={isDashboardDetail}
					/>
				)}
			{navigateToDashboardDetail && (
				<Button
					type="button"
					sx={{
						padding: "4px 16px",
						position: "absolute",
						bottom: "40px",
						height: "32px",
						backgroundColor: palette.darkmodeText.white,
						color: dashboardData.gradientFrom,
						zIndex: 1,
					}}
					onClick={navigateToDashboardDetail}
				>
					{t(Resources.Dashboard.TitleCard.title)}
				</Button>
			)}
		</Box>
	);
};
